import { InputBase, InputBaseProps, Skeleton, TextField, TextFieldProps, Tooltip, TooltipProps } from "@mui/material";
import { forwardRef, ReactNode } from "react";
import { CoreErrorMessage } from "./ErrorMessage";
import styles from "./TextField.module.css";

export type CoreTextFieldProps = Omit<TextFieldProps, "error" | "size" | "inputProps"> & {
	size?: "small" | "medium" | "large";
	error?: ReactNode;
	label?: string;
	useFloatingLabel?: boolean;
	ariaLabel?: string;
	disabledTooltip?: ReactNode;
	maxLength?: number;
	alwaysShowLength?: boolean;
	inputProps?: Omit<Exclude<TextFieldProps["inputProps"], undefined>, "maxLength">;
	slotProps?: {
		tooltip?: Omit<TooltipProps, "title">;
	};
};

export const CoreInputBase = forwardRef<
	HTMLDivElement,
	Omit<InputBaseProps, "size"> & {
		size?: "small" | "medium" | "large";
	}
>(function CoreInputBase({ size = "large", className, ...inputProps }, ref) {
	const { multiline } = inputProps;
	return (
		<InputBase
			className={`${styles.input} ${multiline ? "" : styles[size]} ${className || ""}`}
			{...inputProps}
			ref={ref}
		/>
	);
});

function LengthIndicator({ length, maxLength }: { length: number; maxLength: number }) {
	return (
		<div className={styles.lengthIndicator}>
			{length}/{maxLength}
		</div>
	);
}

const CoreTextField = forwardRef<HTMLDivElement, CoreTextFieldProps>(function CoreTextField(
	{
		disabledTooltip,
		size = "large",
		maxLength,
		alwaysShowLength,
		error,
		className,
		slotProps,
		InputProps,
		inputProps,
		label,
		useFloatingLabel,
		ariaLabel,
		...textFieldProps
	},
	ref,
) {
	const { multiline, helperText, value } = textFieldProps;
	const currLength = typeof value === "string" ? value.length : undefined;
	const showLengthIndicator = maxLength != null && (alwaysShowLength || currLength != null);

	// AutoComplete injects inputProps directly into Textfield which breaks the merging of InputProps, so explicitly merge props here
	// Merging InputProps.inputProps
	const mergedInputProps: TextFieldProps["inputProps"] = {
		...inputProps,
		...InputProps?.inputProps,
	};

	// Merging InputProps
	const mergedOuterInputProps: TextFieldProps["InputProps"] = {
		...InputProps,
		endAdornment: showLengthIndicator ? (
			<>
				{InputProps?.endAdornment}
				<LengthIndicator length={currLength ?? 0} maxLength={maxLength} />
			</>
		) : (
			InputProps?.endAdornment
		),
		className: `${styles.input} ${multiline ? "" : styles[size]} ${showLengthIndicator ? styles.limited : ""} ${InputProps?.className || ""}`,
		inputProps: {
			...mergedInputProps,
			maxLength,
			"aria-label": mergedInputProps["aria-label"] ?? ariaLabel ?? label,
			className: `${styles.input} ${mergedInputProps.className || ""}`,
		},
	};
	const textField = (
		<TextField
			placeholder={useFloatingLabel ? undefined : label}
			label={useFloatingLabel ? label : undefined}
			{...textFieldProps}
			className={`${styles.textField} ${className || ""}`}
			error={!!error}
			InputProps={mergedOuterInputProps}
			helperText={error && typeof error !== "boolean" ? <CoreErrorMessage>{error}</CoreErrorMessage> : helperText}
			ref={ref}
		/>
	);

	return textFieldProps.disabled && disabledTooltip ? (
		<Tooltip {...slotProps?.tooltip} title={disabledTooltip}>
			{textField}
		</Tooltip>
	) : (
		textField
	);
});
export default CoreTextField;

export function CoreTextFieldSkeleton({
	size = "large",
	fullWidth,
	sx,
	className,
}: Pick<CoreTextFieldProps, "size" | "fullWidth" | "sx" | "className">) {
	let height: number;
	switch (size) {
		case "small":
			height = 35;
			break;
		case "medium":
			height = 40;
			break;
		case "large":
			height = 47;
			break;
	}

	return (
		<Skeleton
			className={className}
			variant="rounded"
			height={height}
			width={fullWidth ? "100%" : undefined}
			sx={{ margin: "2px", ...sx }}
		/>
	);
}
